import { Injectable, inject, signal, computed, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import {
  AuthorizationNotifier,
  AuthorizationRequest,
  AuthorizationServiceConfiguration,
  BaseTokenRequestHandler,
  GRANT_TYPE_AUTHORIZATION_CODE,
  GRANT_TYPE_REFRESH_TOKEN,
  RedirectRequestHandler,
  FetchRequestor,
  TokenRequest,
  TokenResponse,
  RevokeTokenRequest,
} from '@openid/appauth';
import { jwtDecode } from 'jwt-decode';
import { StorageService } from './storage.service';
import { SessionStorageBackend } from './session-storage-backend';
import { AngularQueryStringUtils } from './angular-query-string-utils';
import { AuthConfig } from '../models/auth-config.model';
import { IdToken } from '../models/id-token.model';
import { StorageKeys } from '../constants/storage-keys';
import { AuthRoutes } from '../constants/auth-routes';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private router = inject(Router);
  private storage = inject(StorageService);
  private ngZone = inject(NgZone);

  private authHandler = new RedirectRequestHandler(
    new SessionStorageBackend(),
    new AngularQueryStringUtils(),
  );
  private notifier = new AuthorizationNotifier();
  private requestor = new FetchRequestor();

  private autoLogoutTimerRef?: number;
  private refreshTimerRef?: number;

  // Signal-based state
  tokenResponse = signal<TokenResponse | null>(null);
  serviceConfig = signal<AuthorizationServiceConfiguration | null>(null);
  userInfo = signal<IdToken | null>(null);

  // Computed state
  isAuthenticated = computed(() => {
    const token = this.tokenResponse();
    if (!token?.accessToken) return false;
    return token.isValid(-60); // 1 minute buffer
  });

  private normalizeGroups(groups: unknown): string[] {
    if (Array.isArray(groups)) return groups;
    if (typeof groups === 'string') return [groups];
    return [];
  }

  private get isLocalMock(): boolean {
    return !!this.authConfig.issuerUri?.includes('localhost');
  }

  isAdmin = computed(() => {
    if (this.isLocalMock) return true;
    const groups = this.normalizeGroups(this.userInfo()?.groups);
    return groups.some(g => g === 'ADMIN' || g.includes('App-aide-platform-admin-user-group'));
  });

  isWorkbench = computed(() => {
    if (this.isLocalMock) return true;
    const groups = this.normalizeGroups(this.userInfo()?.groups);
    return groups.some(g => g === 'WORKBENCH' || g.includes('App-aide-workbench-user-group'));
  });

  hasAideRole = computed(() => this.isAdmin() || this.isWorkbench());

  authConfig: AuthConfig = {
    clientId: '',
    redirectUri: '',
    scope: 'openid',
    autoLogoutSeconds: 3600,
    tokenRefreshBufferSeconds: 300,
  };

  constructor() {
    this.authHandler.setAuthorizationNotifier(this.notifier);
    this.initializeFromStorage();
    this.setupAutoLogout();
  }

  setConfig(config: AuthConfig): Promise<void> {
    this.authConfig = config;
    return this.fetchServiceConfiguration();
  }

  private initializeFromStorage(): void {
    const storedIssuer = this.storage.read(StorageKeys.ISSUER_URI);
    const configJson = this.storage.read(StorageKeys.OPENID_CONFIG);
    const tokenJson = this.storage.read(StorageKeys.TOKEN_RESPONSE);

    if (configJson) {
      try {
        const config = new AuthorizationServiceConfiguration(JSON.parse(configJson));
        this.serviceConfig.set(config);
      } catch (error) {
        console.error('Error parsing stored config:', error);
      }
    }

    if (tokenJson) {
      try {
        const token = new TokenResponse(JSON.parse(tokenJson));
        const currentIssuer = this.authConfig.issuerUri || this.authConfig.wellKnownUrl;
        // Only validate issuer if authConfig is already set, otherwise load token anyway
        if (!currentIssuer || storedIssuer === currentIssuer) {
          this.tokenResponse.set(token);
          this.updateUserInfo(token);
          this.setupTokenRefresh(token);
        }
      } catch (error) {
        console.error('Error parsing stored token:', error);
      }
    }
  }

  private async fetchServiceConfiguration(): Promise<void> {
    // If direct endpoints are provided, use them (avoids CORS issues)
    if (this.authConfig.authorizationEndpoint && this.authConfig.tokenEndpoint) {
      const config = new AuthorizationServiceConfiguration({
        authorization_endpoint: this.authConfig.authorizationEndpoint,
        token_endpoint: this.authConfig.tokenEndpoint,
        revocation_endpoint: this.authConfig.revocationEndpoint || '',
        userinfo_endpoint: this.authConfig.userinfoEndpoint || '',
      });
      this.serviceConfig.set(config);
      this.storage.write(
        StorageKeys.ISSUER_URI,
        this.authConfig.issuerUri || this.authConfig.authorizationEndpoint,
      );
      this.storage.write(StorageKeys.OPENID_CONFIG, JSON.stringify(config.toJson()));
      return;
    }

    // If wellKnownUrl is provided, fetch from there
    if (this.authConfig.wellKnownUrl) {
      try {
        const response = await fetch(this.authConfig.wellKnownUrl);
        const json = await response.json();
        const config = new AuthorizationServiceConfiguration({
          authorization_endpoint: json.authorization_endpoint,
          token_endpoint: json.token_endpoint,
          revocation_endpoint: json.revocation_endpoint,
          userinfo_endpoint: json.userinfo_endpoint,
        });
        this.serviceConfig.set(config);
        this.storage.write(StorageKeys.ISSUER_URI, this.authConfig.wellKnownUrl);
        this.storage.write(StorageKeys.OPENID_CONFIG, JSON.stringify(config.toJson()));
        return;
      } catch (error) {
        console.error('Failed to fetch well-known configuration:', error);
        return;
      }
    }

    // Fallback to issuerUri for local mock
    const issuerBaseUrl = this.authConfig.issuerUri || '';
    const isLocalMock = issuerBaseUrl.includes('localhost');

    const config = new AuthorizationServiceConfiguration({
      authorization_endpoint: isLocalMock
        ? `${issuerBaseUrl}/authorize`
        : `${issuerBaseUrl}/as/authorization.oauth2`,
      token_endpoint: isLocalMock ? `${issuerBaseUrl}/token` : `${issuerBaseUrl}/as/token.oauth2`,
      revocation_endpoint: isLocalMock
        ? `${issuerBaseUrl}/revoke`
        : `${issuerBaseUrl}/as/revoke_token.oauth2`,
      userinfo_endpoint: isLocalMock
        ? `${issuerBaseUrl}/userinfo`
        : `${issuerBaseUrl}/idp/userinfo.openid`,
    });

    this.serviceConfig.set(config);
    this.storage.write(StorageKeys.ISSUER_URI, this.authConfig.issuerUri || '');
    this.storage.write(StorageKeys.OPENID_CONFIG, JSON.stringify(config.toJson()));
  }

  authorize(): void {
    const config = this.serviceConfig();
    if (!config) {
      return;
    }

    const request = new AuthorizationRequest(
      {
        client_id: this.authConfig.clientId,
        redirect_uri: this.authConfig.redirectUri,
        scope: this.authConfig.scope,
        response_type: AuthorizationRequest.RESPONSE_TYPE_CODE,
        extras: { prompt: 'login' },
      },
      undefined,
      true,
    ); // Enable PKCE

    this.authHandler.performAuthorizationRequest(config, request);
  }

  async completeAuthorizationRequest(): Promise<TokenResponse> {
    return new Promise((resolve, reject) => {
      const config = this.serviceConfig();
      if (!config) {
        reject(new Error('Service configuration not loaded'));
        return;
      }

      this.notifier.setAuthorizationListener(async (request, response, error) => {
        if (error) {
          reject(error);
          return;
        }

        if (!response?.code) {
          reject(new Error('No authorization code received'));
          return;
        }

        if (!request) {
          reject(new Error('No authorization request found'));
          return;
        }

        const tokenHandler = new BaseTokenRequestHandler(this.requestor);

        // Build token request with PKCE verifier
        const extras: Record<string, string> = {};
        if (request.internal?.['code_verifier']) {
          extras['code_verifier'] = request.internal['code_verifier'];
        }

        const tokenRequest = new TokenRequest({
          client_id: this.authConfig.clientId,
          redirect_uri: this.authConfig.redirectUri,
          grant_type: GRANT_TYPE_AUTHORIZATION_CODE,
          code: response.code,
          extras,
        });

        try {
          const tokenResponse = await tokenHandler.performTokenRequest(config, tokenRequest);
          this.handleTokenResponse(tokenResponse);
          resolve(tokenResponse);
        } catch (err) {
          reject(err);
        }
      });

      this.authHandler.completeAuthorizationRequestIfPossible();
    });
  }

  private handleTokenResponse(token: TokenResponse): void {
    this.tokenResponse.set(token);
    this.storage.write(StorageKeys.TOKEN_RESPONSE, JSON.stringify(token.toJson()));
    this.updateUserInfo(token);
    this.setupTokenRefresh(token);
    this.updateLastActivity();
  }

  private updateUserInfo(token: TokenResponse): void {
    if (token.idToken) {
      try {
        const decoded = jwtDecode<IdToken>(token.idToken);
        this.userInfo.set(decoded);
      } catch (error) {
        console.error('Error decoding ID token:', error);
      }
    }
  }

  private setupTokenRefresh(token: TokenResponse): void {
    if (this.refreshTimerRef) {
      clearTimeout(this.refreshTimerRef);
    }

    if (!token.issuedAt || !token.expiresIn) return;

    const expiresAt = token.issuedAt + token.expiresIn;
    const now = Math.floor(Date.now() / 1000);
    const refreshIn = (expiresAt - now - this.authConfig.tokenRefreshBufferSeconds) * 1000;

    if (refreshIn > 0) {
      this.ngZone.runOutsideAngular(() => {
        this.refreshTimerRef = window.setTimeout(() => {
          this.ngZone.run(() => this.refreshToken());
        }, refreshIn);
      });
    } else {
      // Token already expired or about to expire, refresh immediately
      console.warn('Token expired or expiring soon, refreshing immediately');
      this.refreshToken();
    }
  }

  private async refreshToken(): Promise<void> {
    const config = this.serviceConfig();
    const token = this.tokenResponse();

    if (!config || !token?.refreshToken) {
      await this.signOut();
      return;
    }

    const tokenHandler = new BaseTokenRequestHandler(this.requestor);
    const request = new TokenRequest({
      client_id: this.authConfig.clientId,
      redirect_uri: this.authConfig.redirectUri,
      grant_type: GRANT_TYPE_REFRESH_TOKEN,
      refresh_token: token.refreshToken,
    });

    try {
      const newToken = await tokenHandler.performTokenRequest(config, request);
      this.handleTokenResponse(newToken);
    } catch (error) {
      console.error('Token refresh failed:', error);
      await this.signOut();
    }
  }

  private setupAutoLogout(): void {
    if (this.autoLogoutTimerRef) {
      clearInterval(this.autoLogoutTimerRef);
    }

    this.ngZone.runOutsideAngular(() => {
      this.autoLogoutTimerRef = window.setInterval(() => {
        // Only check if user is authenticated
        if (!this.isAuthenticated()) {
          return;
        }

        const lastActivity = this.storage.read(StorageKeys.LAST_ACTIVITY);
        if (!lastActivity) {
          // No activity tracked yet, set it now
          this.updateLastActivity();
          return;
        }

        const elapsed = Date.now() - parseInt(lastActivity, 10);
        if (elapsed >= this.authConfig.autoLogoutSeconds * 1000) {
          this.ngZone.run(() => this.signOut());
        }
      }, 5000);
    });
  }

  updateLastActivity(): void {
    this.storage.write(StorageKeys.LAST_ACTIVITY, Date.now().toString());
  }

  saveRedirectUrl(url: string): void {
    this.storage.write(StorageKeys.REDIRECT_URL, url);
  }

  getRedirectUrl(): string | null {
    return this.storage.read(StorageKeys.REDIRECT_URL);
  }

  clearRedirectUrl(): void {
    this.storage.remove(StorageKeys.REDIRECT_URL);
  }

  signIn(): void {
    this.authorize();
  }

  async signOut(): Promise<void> {
    await this.clearSession();
    await this.router.navigate([AuthRoutes.SIGN_IN]);
  }

  async clearSession(): Promise<void> {
    const config = this.serviceConfig();
    const token = this.tokenResponse();

    if (config && token?.refreshToken) {
      const tokenHandler = new BaseTokenRequestHandler(this.requestor);
      const request = new RevokeTokenRequest({
        token: token.refreshToken,
        client_id: this.authConfig.clientId,
        token_type_hint: 'refresh_token',
      });

      try {
        await tokenHandler.performRevokeTokenRequest(config, request);
      } catch (error) {
        console.error('Token revocation failed:', error);
      }
    }

    this.tokenResponse.set(null);
    this.userInfo.set(null);
    this.storage.clear();

    if (this.autoLogoutTimerRef) {
      clearInterval(this.autoLogoutTimerRef);
    }
    if (this.refreshTimerRef) {
      clearTimeout(this.refreshTimerRef);
    }
  }
}
